import React, { useRef, Children, Component } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { EyeIcon, HandIcon, WindIcon, FingerprintIcon, UtensilsIcon, CheckCircleIcon, BookOpenIcon, LayersIcon, AnchorIcon, MessageSquareIcon, CalendarIcon, ClipboardListIcon } from 'lucide-react';
export function MethodSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const ladderSteps = [{
    step: 6,
    name: 'Eating',
    icon: CheckCircleIcon,
    color: '#2D4A3E'
  }, {
    step: 5,
    name: 'Tasting',
    icon: UtensilsIcon,
    color: '#5B8A7A'
  }, {
    step: 4,
    name: 'Touching',
    icon: FingerprintIcon,
    color: '#5B8A7A'
  }, {
    step: 3,
    name: 'Smelling',
    icon: WindIcon,
    color: '#8BA888'
  }, {
    step: 2,
    name: 'Interacting',
    icon: HandIcon,
    color: '#8BA888'
  }, {
    step: 1,
    name: 'Tolerating',
    icon: EyeIcon,
    color: '#8BA888'
  }];
  const deliverables = [{
    title: 'The 56-Page Protocol Guide',
    description: 'The complete science of "The Biology of No" and how to reset your table energy.',
    icon: BookOpenIcon
  }, {
    title: 'The 6-Step Exposure Ladder',
    description: 'Exactly how to move a child from "refusal" to "tasting" without a single tear.',
    icon: LayersIcon
  }, {
    title: "The 'Safe Food' Anchor System",
    description: 'How to plate meals so your child feels secure enough to explore.',
    icon: AnchorIcon
  }, {
    title: 'The Mealtime Script Cheat Sheet',
    description: 'Exactly what to say when they say "Ew" to stay in the power seat without being the "bad guy."',
    icon: MessageSquareIcon
  }, {
    title: 'The 7-Day Reset Calendar',
    description: 'A day-by-day plan to transition from short-order cook to lead diner.',
    icon: CalendarIcon
  }, {
    title: 'The Interaction Tracker',
    description: 'A worksheet to see the "hidden" progress your child is making every day.',
    icon: ClipboardListIcon
  }];
  return <section ref={ref} className="w-full bg-[#F0F5EE] py-10 md:py-16 px-4" data-id="element-95">
      <div className="max-w-3xl mx-auto" data-id="element-96">
        {/* The Exposure Ladder */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="mb-14" data-id="element-97">
          <h2 className="font-['Playfair_Display'] text-2xl md:text-4xl font-bold text-[#2D4A3E] mb-4 text-center" data-id="element-98">
            Introducing: The Exposure Ladder Methodology
          </h2>
          <p className="font-['Source_Sans_3'] text-base md:text-lg text-[#3A3A3A] leading-relaxed mb-7 text-center max-w-2xl mx-auto" data-id="element-99">
            Children don't refuse food because they are "bad"—they refuse
            because their brain perceives new textures as a physical threat. The
            Exposure Ladder breaks the "jump" from looking to eating into 6
            tiny, non-threatening rungs:
          </p>

          {/* Ladder Visual */}
          <div className="max-w-md mx-auto mb-7" data-id="element-100">
            <div className="relative" data-id="element-101">
              {ladderSteps.map((step, index) => {
              const IconComponent = step.icon;
              return <motion.div key={step.step} initial={{
                opacity: 0,
                x: -30
              }} animate={isInView ? {
                opacity: 1,
                x: 0
              } : {}} transition={{
                duration: 0.5,
                delay: 0.3 + (5 - index) * 0.1
              }} className="flex items-center gap-4 mb-3" style={{
                marginLeft: `${(6 - step.step) * 12}px`
              }} data-id="element-102">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold shadow-md" style={{
                  backgroundColor: step.color
                }} data-id="element-103">
                      {step.step}
                    </div>
                    <div className="flex items-center gap-3 bg-white px-4 py-3 rounded-lg shadow-sm flex-1" data-id="element-104">
                      <IconComponent className="w-5 h-5" style={{
                    color: step.color
                  }} data-id="element-105" />
                      <span className="font-['Source_Sans_3'] font-semibold text-[#3A3A3A]" data-id="element-106">
                        {step.name}
                      </span>
                    </div>
                  </motion.div>;
            })}
            </div>
          </div>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.9
        }} className="font-['Source_Sans_3'] text-lg md:text-xl text-[#2D4A3E] font-medium text-center italic" data-id="element-107">
            "We stop asking them to eat. We start asking them to climb. By
            lowering the threat level, the 'No' naturally begins to soften."
          </motion.p>
        </motion.div>

        {/* What You Get */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7,
        delay: 0.3
      }} data-id="element-108">
          <h2 className="font-['Playfair_Display'] text-2xl md:text-4xl font-bold text-[#2D4A3E] mb-6 text-center" data-id="element-109">
            Everything Inside the Protocol
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-110">
            {deliverables.map((item, index) => {
            const IconComponent = item.icon;
            return <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} animate={isInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.5,
              delay: 0.5 + index * 0.1
            }} className="bg-white p-5 rounded-xl shadow-sm border border-[#E8DDD0]" data-id="element-111">
                  <div className="flex items-start gap-4" data-id="element-112">
                    <div className="w-10 h-10 rounded-full bg-[#F0F5EE] flex items-center justify-center flex-shrink-0" data-id="element-113">
                      <CheckCircleIcon className="w-5 h-5 text-[#5B8A7A]" data-id="element-114" />
                    </div>
                    <div data-id="element-115">
                      <h3 className="font-['Playfair_Display'] font-semibold text-[#2D4A3E] text-lg mb-1" data-id="element-116">
                        {item.title}
                      </h3>
                      <p className="font-['Source_Sans_3'] text-sm text-[#3A3A3A] leading-relaxed" data-id="element-117">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>
        </motion.div>
      </div>
    </section>;
}